import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { Store } from '@ngxs/store';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { CC_CONSTANTS } from '../../assets/constants';
import { convertNumber } from 'src/app/shared/all.helpers';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorAec9Cream,
    colorBlack,
    colorWhite,
} from 'src/app/shared/all.constants';
import {
    ENEDIS_CONNECTIONS_COMMISSIONING_RATE_CHART,
    ENEDIS_CONNECTIONS_COUNT_CHART,
    ENEDIS_CONNECTIONS_ESTIMATION_NOTICE_CHART,
    ENEDIS_CONNECTIONS_KPIS,
    ENEDIS_CONNECTIONS_PRODUCERS_COUNT_CHART,
} from '../../assets/keys';

const KEYS = [
    ENEDIS_CONNECTIONS_KPIS,
    ENEDIS_CONNECTIONS_COUNT_CHART,
    ENEDIS_CONNECTIONS_PRODUCERS_COUNT_CHART,
    ENEDIS_CONNECTIONS_ESTIMATION_NOTICE_CHART,
    ENEDIS_CONNECTIONS_COMMISSIONING_RATE_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    connectionsWithdrawalTotal?: number;
    connectionsWithdrawalTotalEvolution?: number;

    connectionsInjectionTotal?: number;
    connectionsInjectionTotalEvolution?: number;

    connectionsInfraNumber?: number;
    connectionsInfraNumberEvolution?: number;

    powerInjectionConnections?: number;
    powerInjectionConnectionsEvolution?: number;
}

interface ConnectionsCountData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    individualLvUnder36?: number;
    collectiveLvUnder36?: number;
    lvUnder36WithAdaptedNetwork?: number;
    lvOver36?: number;
    mv?: number;
    total?: number;
}

interface ConnectionsProducersCountData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    totalInjection?: number;
    individualLvUnder36WithoutAdaptation?: number;
    individualLvUnder36WithAdaptation?: number;
    lvOver36Under250?: number;
    mvOver250?: number;
    totalPower?: number;
}

interface ConnectionsEstimationNoticeData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    consumption?: number;
    production?: number;
}

interface ConnectionsCommissioningRateData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    xYear?: number;
    commissioningRate?: number;
    consumptionTelephoneReception?: number;
    consumptionConnectionRate?: number;
    productionConnectionRate?: number;
    productionTelephoneReception?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-enedis-connections',
    templateUrl: './enedis-connections.component.html',
    styleUrls: ['./enedis-connections.component.scss'],
})
export class EnedisConnectionsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.connections.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CONNECTIONS_KPIS),
    ) as Signal<KpiData[]>;

    public connectionsCountChartData: Signal<ConnectionsCountData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ENEDIS_CONNECTIONS_COUNT_CHART),
    ) as Signal<ConnectionsCountData[]>;
    public connectionsCountChartError: boolean = true;
    public connectionsCountChartSeries: ApexAxisChartSeries = [];
    public connectionsCountDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        // formatter: this.setDataLabelsFormatter([0, 1, 2]),
    };
    public connectionsCountXaxisCategories: ApexXAxis['categories'];
    public connectionsCountYaxis: ApexYAxis[] = [];

    public connectionsProducersCountChartData: Signal<ConnectionsProducersCountData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ENEDIS_CONNECTIONS_PRODUCERS_COUNT_CHART),
        ) as Signal<ConnectionsProducersCountData[]>;
    public connectionsProducersCountChartError: boolean = true;
    public connectionsProducersCountChartSeries: ApexAxisChartSeries = [];
    public connectionsProducersCountDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(
            this.texts.connectionsProducersCountChart.series[4].name,
        ),
        // formatter: this.setDataLabelsFormatter([0, 1, 2]),
    };
    public connectionsProducersCountXaxisCategories: ApexXAxis['categories'];
    public connectionsProducersCountYaxis: ApexYAxis[] = [];
    public connectionsProducersCountLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec5Brown,
                colorAec6Gray,
                colorBlack,
                colorAec8RedDark,
            ],
        },
    };
    public connectionsProducersCountCustomTooltip: ApexTooltip['custom'];

    public connectionsEstimationNoticeChartData: Signal<ConnectionsEstimationNoticeData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ENEDIS_CONNECTIONS_ESTIMATION_NOTICE_CHART),
        ) as Signal<ConnectionsEstimationNoticeData[]>;
    public connectionsEstimationNoticeChartError: boolean = true;
    public connectionsEstimationNoticeChartSeries: ApexAxisChartSeries = [];
    public connectionsEstimationNoticeDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: 1,
            1: 1,
        }),
    };
    public connectionsEstimationNoticeXaxisCategories: ApexXAxis['categories'];
    public connectionsEstimationNoticeYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.connectionsEstimationNoticeChart.series[0].name,
                this.texts.connectionsEstimationNoticeChart.series[1].name,
            ],
            min: 0,
            title: {
                text: this.texts.connectionsEstimationNoticeChart.yaxisTitleLeft,
            },
        },
    ];
    public connectionsEstimationNoticeGrid: ApexGrid = { padding: { left: 30 } };

    public connectionsCommissioningRateChartData: Signal<ConnectionsCommissioningRateData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ENEDIS_CONNECTIONS_COMMISSIONING_RATE_CHART),
        ) as Signal<ConnectionsCommissioningRateData[]>;
    public connectionsCommissioningRateChartError: boolean = true;
    public connectionsCommissioningRateChartSeries: ApexAxisChartSeries = [];
    public connectionsCommissioningRateDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: (val: number) =>
            convertNumber(val, this.texts.connectionsCommissioningRateChart.precision) + '%',
        textAnchor: 'middle',
        style: {
            fontSize: '12px',
            colors: [
                colorAec6Gray,
                colorAec7RedLight,
                colorAec4Orange,
                colorAec3Green,
                colorAec1BlueLight,
            ],
        },
        background: {
            enabled: true,
            foreColor: colorWhite,
            borderColor: colorWhite,
        },
    };
    public connectionsCommissioningRateXaxisCategories: ApexXAxis['categories'];
    public connectionsCommissioningRateYaxis: ApexYAxis[] = [
        {
            seriesName: [
                this.texts.connectionsCommissioningRateChart.series[0].name[0],
                this.texts.connectionsCommissioningRateChart.series[1].name[0],
            ],
            min: 0,
            labels: {
                maxWidth: 320,
            },
        },
    ];
    public connectionsCommissioningRatePlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            dataLabels: {
                position: 'center',
            },
        },
    };

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const connectionsCountChartData = this.connectionsCountChartData();
            this.connectionsCountChartError = this.checkForError(connectionsCountChartData, [
                'xYear',
                'individualLvUnder36',
                'collectiveLvUnder36',
                'lvUnder36WithAdaptedNetwork',
                'lvOver36',
                'mv',
                'total',
            ]);
            if (!this.connectionsCountChartError) {
                this.connectionsCountXaxisCategories = connectionsCountChartData.map(
                    (data) => data.xYear,
                );

                const maxValue = connectionsCountChartData.reduce(
                    (max, d) =>
                        Math.max(
                            max,
                            (d.collectiveLvUnder36 ?? 0) +
                                (d.lvOver36 ?? 0) +
                                (d.lvUnder36WithAdaptedNetwork ?? 0) +
                                (d.mv ?? 0),
                        ),
                    0,
                );
                const maxTotal = connectionsCountChartData.reduce(
                    (max, d) => Math.max(max, d.total ?? 0, d.individualLvUnder36 ?? 0),
                    0,
                );
                this.connectionsCountYaxis = [
                    {
                        seriesName: [
                            this.texts.connectionsCountChart.series[0].name,
                            this.texts.connectionsCountChart.series[1].name,
                            this.texts.connectionsCountChart.series[2].name,
                            this.texts.connectionsCountChart.series[5].name,
                        ],
                        min: 0,
                        max: Math.ceil(maxValue / 100 + 1) * 100,
                        forceNiceScale: true,
                        tickAmount: 4,
                        decimalsInFloat: 0,
                        title: {
                            text: this.texts.connectionsCountChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: [
                            this.texts.connectionsCountChart.series[3].name,
                            this.texts.connectionsCountChart.series[4].name,
                        ],
                        min: 0,
                        max: Math.ceil(maxTotal / 100 + 1) * 100,
                        decimalsInFloat: 0,
                        opposite: true,
                        title: {
                            text: this.texts.connectionsCountChart.yaxisTitleRight,
                        },
                    },
                ];
                this.connectionsCountChartSeries = [
                    {
                        name: this.texts.connectionsCountChart.series[0].name,
                        type: 'column',
                        data: connectionsCountChartData.map((data) => data.collectiveLvUnder36!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.connectionsCountChart.series[1].name,
                        type: 'column',
                        data: connectionsCountChartData.map(
                            (data) => data.lvUnder36WithAdaptedNetwork!,
                        ),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.connectionsCountChart.series[2].name,
                        type: 'column',
                        data: connectionsCountChartData.map((data) => data.lvOver36!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.connectionsCountChart.series[5].name,
                        type: 'column',
                        data: connectionsCountChartData.map((data) => data.mv!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.connectionsCountChart.series[3].name,
                        type: 'line',
                        data: connectionsCountChartData.map((data) => data.total!),
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.connectionsCountChart.series[4].name,
                        type: 'line',
                        data: connectionsCountChartData.map((data) => data.individualLvUnder36!),
                        color: colorAec3Green,
                    },
                ];
                this.connectionsCountDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5];
            }

            const connectionsProducersCountChartData = this.connectionsProducersCountChartData();
            this.connectionsProducersCountChartError = this.checkForError(
                connectionsProducersCountChartData,
                [
                    'xYear',
                    'individualLvUnder36WithoutAdaptation',
                    'individualLvUnder36WithAdaptation',
                    'lvOver36Under250',
                    'mvOver250',
                    'totalPower',
                ],
            );
            if (!this.connectionsProducersCountChartError) {
                this.connectionsProducersCountXaxisCategories =
                    connectionsProducersCountChartData.map((data) => data.xYear);
                const totales = this.getTotales(connectionsProducersCountChartData, [
                    'individualLvUnder36WithoutAdaptation',
                    'individualLvUnder36WithAdaptation',
                    'lvOver36Under250',
                    'mvOver250',
                ]);

                const totalesMax = totales.reduce((max, t) => Math.max(max, t), 0);
                const totalPowerMax = connectionsProducersCountChartData.reduce(
                    (max, t) => Math.max(max, t.totalPower ?? 0),
                    0,
                );
                this.connectionsProducersCountYaxis = [
                    {
                        seriesName: [
                            this.texts.connectionsProducersCountChart.series[0].name,
                            this.texts.connectionsProducersCountChart.series[1].name,
                            this.texts.connectionsProducersCountChart.series[2].name,
                            this.texts.connectionsProducersCountChart.series[3].name,
                            this.texts.connectionsProducersCountChart.series[4].name,
                        ],
                        min: 0,
                        max: Math.round(totalesMax / 40 + 1) * 40,
                        tickAmount: 4,
                        forceNiceScale: true,
                        title: {
                            text: this.texts.connectionsProducersCountChart.yaxisTitleLeft,
                        },
                    },
                    {
                        seriesName: [this.texts.connectionsProducersCountChart.series[5].name],
                        min: 0,
                        max: Math.round(totalPowerMax / 40 + 1) * 40,
                        forceNiceScale: true,
                        tickAmount: 4,
                        opposite: true,
                        title: {
                            text: this.texts.connectionsProducersCountChart.yaxisTitleRight,
                        },
                    },
                ];

                this.connectionsProducersCountChartSeries = [
                    {
                        name: this.texts.connectionsProducersCountChart.series[0].name,
                        type: 'column',
                        data: connectionsProducersCountChartData.map(
                            (data) => data.individualLvUnder36WithoutAdaptation!,
                        ),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.connectionsProducersCountChart.series[1].name,
                        type: 'column',
                        data: connectionsProducersCountChartData.map(
                            (data) => data.individualLvUnder36WithAdaptation!,
                        ),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.connectionsProducersCountChart.series[2].name,
                        type: 'column',
                        data: connectionsProducersCountChartData.map(
                            (data) => data.lvOver36Under250!,
                        ),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.connectionsProducersCountChart.series[3].name,
                        type: 'column',
                        data: connectionsProducersCountChartData.map((data) => data.mvOver250!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.connectionsProducersCountChart.series[4].name,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                    {
                        name: this.texts.connectionsProducersCountChart.series[5].name,
                        type: 'line',
                        data: connectionsProducersCountChartData.map((data) => data.totalPower!),
                        color: colorAec8RedDark,
                    },
                ];
                this.connectionsProducersCountDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5];
            }

            const connectionsEstimationNoticeChartData =
                this.connectionsEstimationNoticeChartData();
            this.connectionsEstimationNoticeChartError = this.checkForError(
                connectionsEstimationNoticeChartData,
                ['xYear', 'consumption', 'production'],
            );
            if (!this.connectionsEstimationNoticeChartError) {
                this.connectionsEstimationNoticeXaxisCategories =
                    connectionsEstimationNoticeChartData.map((data) => data.xYear);
                this.connectionsEstimationNoticeChartSeries = [
                    {
                        name: this.texts.connectionsEstimationNoticeChart.series[0].name,
                        type: 'line',
                        data: connectionsEstimationNoticeChartData.map((data) => data.consumption!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.connectionsEstimationNoticeChart.series[1].name,
                        type: 'line',
                        data: connectionsEstimationNoticeChartData.map((data) => data.production!),
                        color: colorAec1BlueLight,
                    },
                ];
                this.connectionsEstimationNoticeDataLabels.enabledOnSeries = [0, 1];
            }

            const connectionsCommissioningRateChartData =
                this.connectionsCommissioningRateChartData();
            this.connectionsCommissioningRateChartError = this.checkForError(
                connectionsCommissioningRateChartData,
                [
                    'xYear',
                    'commissioningRate',
                    'consumptionTelephoneReception',
                    'consumptionConnectionRate',
                    'productionConnectionRate',
                    'productionTelephoneReception',
                ],
            );
            if (!this.connectionsCommissioningRateChartError) {
                const transposedData = connectionsCommissioningRateChartData.map((row) => ({
                    name: row.xYear!,
                    y: [
                        row.commissioningRate!,
                        row.productionConnectionRate!,
                        row.consumptionConnectionRate!,
                        row.productionTelephoneReception!,
                        row.consumptionTelephoneReception!,
                    ],
                }));
                this.connectionsCommissioningRateXaxisCategories =
                    this.texts.connectionsCommissioningRateChart.series.map((serie) => serie.name);
                this.connectionsCommissioningRateChartSeries = [
                    {
                        name: transposedData[0].name.toString(),
                        data: transposedData[0].y,
                        color: colorAec6Gray,
                    },
                    {
                        name: transposedData[1].name.toString(),
                        data: transposedData[1].y,
                        color: colorAec7RedLight,
                    },
                    {
                        name: transposedData[2].name.toString(),
                        data: transposedData[2].y,
                        color: colorAec4Orange,
                    },
                    {
                        name: transposedData[3].name.toString(),
                        data: transposedData[3].y,
                        color: colorAec3Green,
                    },
                    {
                        name: transposedData[4].name.toString(),
                        data: transposedData[4].y,
                        color: colorAec1BlueLight,
                    },
                ];
                this.connectionsCommissioningRateDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }
        });
    }

    ngOnInit() {
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.connectionsProducersCountCustomTooltip =
            this.setConnectionsProducersCountCustomTooltip();
    }

    public setConnectionsProducersCountCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.connectionsProducersCountChart.precision;

            const lvUnder36WithoutAdaptationSerie = w.globals.initialSeries[0];
            const {
                name: lvUnder36WithoutAdaptationName,
                color: lvUnder36WithoutAdaptationColor,
                valueStringified: lvUnder36WithoutAdaptationStringified,
            } = this.setCustomTooltipData(
                precision,
                lvUnder36WithoutAdaptationSerie,
                dataPointIndex,
            );

            const lvUnder36WithAdaptationSerie = w.globals.initialSeries[1];
            const {
                name: lvUnder36WithAdaptationName,
                color: lvUnder36WithAdaptationColor,
                valueStringified: lvUnder36WithAdaptationStringified,
            } = this.setCustomTooltipData(precision, lvUnder36WithAdaptationSerie, dataPointIndex);

            const lvOver36Under250Serie = w.globals.initialSeries[2];
            const {
                name: lvOver36Under250Name,
                color: lvOver36Under250Color,
                valueStringified: lvOver36Under250Stringified,
            } = this.setCustomTooltipData(precision, lvOver36Under250Serie, dataPointIndex);

            const mvOver250Serie = w.globals.initialSeries[3];
            const {
                name: mvOver250Name,
                color: mvOver250Color,
                valueStringified: mvOver250Stringified,
            } = this.setCustomTooltipData(precision, mvOver250Serie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[4];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const totalPowerSerie = w.globals.initialSeries[5];
            const {
                name: totalPowerName,
                color: totalPowerColor,
                valueStringified: totalPowerStringified,
            } = this.setCustomTooltipData(precision, totalPowerSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: lvUnder36WithoutAdaptationColor,
                    name: lvUnder36WithoutAdaptationName,
                    value: lvUnder36WithoutAdaptationStringified,
                },
                {
                    color: lvUnder36WithAdaptationColor,
                    name: lvUnder36WithAdaptationName,
                    value: lvUnder36WithAdaptationStringified,
                },
                {
                    color: lvOver36Under250Color,
                    name: lvOver36Under250Name,
                    value: lvOver36Under250Stringified,
                },
                {
                    color: mvOver250Color,
                    name: mvOver250Name,
                    value: mvOver250Stringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
                {
                    color: totalPowerColor,
                    name: `Puissance totale des producteurs raccordés dans l'année`,
                    value: `${totalPowerStringified} kVA`,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
}
